// Homepage pricing section: single custom-project path, no annual tiers.
import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, MessageSquare, FileText, Rocket, LifeBuoy } from 'lucide-react';
import { useScrollReveal } from '@/hooks/use-scroll-reveal';
import { Button } from '@/components/ui/button';

const steps = [
  { icon: MessageSquare, title: 'Free discovery call', body: 'A 30 minute conversation about the problem and what a good outcome looks like.' },
  { icon: FileText, title: 'Fixed-price proposal', body: 'Once the scope is clear, a written proposal with a fixed price and timeline.' },
  { icon: Rocket, title: 'Build and launch', body: 'Weekly demos so you see progress every Friday and can steer the work as it ships.' },
  { icon: LifeBuoy, title: 'Ongoing support', body: 'We stay on after launch: hosting, monitoring, fixes, and new features as you grow.' },
];

export const PricingSection = () => {
  const { elementRef, getRevealStyle } = useScrollReveal();

  return (
    <section className="section">
      <div className="container">
        <div className="section-header">
          <div className="micro-label mb-4">PRICING</div>
          <h2 className="section-title">Pricing built around your project, not a package</h2>
          <p className="section-subtitle">
            Every app we build is scoped, priced, and delivered as a single fixed-price project. No annual tiers, no surprise invoices.
          </p>
        </div>

        <div ref={elementRef} className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4 max-w-6xl mx-auto">
          {steps.map((step, index) => {
            const Icon = step.icon;
            return (
              <div
                key={step.title}
                className="p-6 rounded-2xl bg-surface/50 border border-border hover:border-primary/30 transition-colors h-full"
                style={getRevealStyle(index + 1)}
              >
                <div className="w-10 h-10 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center mb-4">
                  <Icon className="w-5 h-5 text-primary" />
                </div>
                <h3 className="font-semibold text-foreground text-base mb-2">{step.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{step.body}</p>
              </div>
            );
          })}
        </div>

        <div className="text-center mt-12">
          <Link to="/contact?plan=Custom%20App%20%2F%20Web%20App%20%2F%20MVP">
            <Button size="lg" className="group">
              Request a quote
              <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Link>
          <div className="mt-4">
            <Link to="/pricing" className="text-sm text-muted-foreground hover:text-primary transition-colors">
              See how pricing works →
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};
